import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import SpeedGauge from './SpeedGauge'
import UserInfo from './UserInfo'
import TestResults from './TestResults'
import ShareResults from './ShareResults'
import { useSpeedTest } from '../hooks/useSpeedTest'
import { Play, RotateCcw, Download, Upload, Gauge, Activity, Globe, Shield, ChevronDown, Zap, Lock, CheckCircle } from 'lucide-react'

export interface SpeedTestResult {
  downloadSpeed: number
  uploadSpeed: number
  ping: number
  jitter: number
  timestamp: number
}

// Demo 数据，用于首次访问时展示
const DEMO_RESULT: SpeedTestResult = {
  downloadSpeed: 156.8,
  uploadSpeed: 48.3,
  ping: 12,
  jitter: 3,
  timestamp: Date.now()
}

// Feature items for the features section
const features = [
  { icon: Download, titleKey: 'feature1Title', descKey: 'feature1Desc', color: 'text-apple-blue' },
  { icon: Upload, titleKey: 'feature2Title', descKey: 'feature2Desc', color: 'text-apple-green' },
  { icon: Gauge, titleKey: 'feature3Title', descKey: 'feature3Desc', color: 'text-apple-orange' },
  { icon: Activity, titleKey: 'feature4Title', descKey: 'feature4Desc', color: 'text-apple-purple' },
  { icon: Globe, titleKey: 'feature5Title', descKey: 'feature5Desc', color: 'text-apple-teal' },
  { icon: Shield, titleKey: 'feature6Title', descKey: 'feature6Desc', color: 'text-apple-red' },
]

// Why choose us items
const whyChooseItems = [
  { icon: Zap, titleKey: 'whyChoose1Title', descKey: 'whyChoose1Desc', color: 'text-apple-blue' },
  { icon: Globe, titleKey: 'whyChoose2Title', descKey: 'whyChoose2Desc', color: 'text-apple-green' },
  { icon: Gauge, titleKey: 'whyChoose3Title', descKey: 'whyChoose3Desc', color: 'text-apple-orange' },
  { icon: Lock, titleKey: 'whyChoose4Title', descKey: 'whyChoose4Desc', color: 'text-apple-purple' },
]

// How to use steps
const howToSteps = [
  'howToStep1',
  'howToStep2',
  'howToStep3',
  'howToStep4',
  'howToStep5',
]

// FAQ items
const faqItems = [
  { questionKey: 'faq1Question', answerKey: 'faq1Answer' },
  { questionKey: 'faq2Question', answerKey: 'faq2Answer' },
  { questionKey: 'faq3Question', answerKey: 'faq3Answer' },
  { questionKey: 'faq4Question', answerKey: 'faq4Answer' },
  { questionKey: 'faq5Question', answerKey: 'faq5Answer' },
  { questionKey: 'faq6Question', answerKey: 'faq6Answer' },
]

// FAQ Item component with accordion
const FAQItem: React.FC<{ questionKey: string; answerKey: string; index: number }> = ({ questionKey, answerKey, index }) => {
  const { t } = useTranslation()

  return (
    <motion.details
      className="group border-b border-apple-gray-200 last:border-b-0"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <summary
        className="w-full py-5 flex items-center justify-between text-left hover:bg-apple-gray-50 transition-colors duration-200 px-2 -mx-2 rounded-lg cursor-pointer list-none"
      >
        <span className="font-medium text-apple-gray-900 pr-4">{t(questionKey)}</span>
        <span className="flex-shrink-0 transition-transform duration-200 group-open:rotate-180">
          <ChevronDown className="w-5 h-5 text-apple-gray-500" />
        </span>
      </summary>
      <p className="pb-5 text-apple-gray-600 leading-relaxed px-2">{t(answerKey)}</p>
    </motion.details>
  )
}

const SpeedTest: React.FC = () => {
  const { t } = useTranslation()
  const {
    isRunning,
    currentTest,
    progress,
    results,
    error,
    startTest,
    resetTest
  } = useSpeedTest()

  const [showResults, setShowResults] = useState(true)
  const [isDemo, setIsDemo] = useState(true)

  // 当有真实测试结果时，切换到真实数据显示
  useEffect(() => {
    if (results && !isRunning) {
      setIsDemo(false)
      setShowResults(true)
    }
  }, [results, isRunning])

  // 测试开始时隐藏结果
  useEffect(() => {
    if (isRunning) {
      setShowResults(false)
    }
  }, [isRunning])

  // 当前显示的结果（demo 或真实结果）
  const displayResults = isDemo ? DEMO_RESULT : results

  const handleStartTest = () => {
    setIsDemo(false)
    setShowResults(false)
    startTest()
  }

  const handleTestAgain = () => {
    setIsDemo(false)
    setShowResults(false)
    resetTest()
  }

  const getSpeedRating = (speed: number): string => {
    if (speed >= 100) return t('excellent')
    if (speed >= 50) return t('good')
    if (speed >= 25) return t('fair')
    return t('poor')
  }

  const getSpeedRatingColor = (speed: number): string => {
    if (speed >= 100) return 'text-apple-green'
    if (speed >= 50) return 'text-apple-blue'
    if (speed >= 25) return 'text-apple-orange'
    return 'text-apple-red'
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Hero Section */}
      <motion.section
        className="text-center mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl md:text-4xl font-bold text-apple-gray-900 mb-4">
          {t('heroTitle')}
        </h1>
        <p className="text-lg text-apple-gray-600 leading-relaxed max-w-3xl mx-auto">
          {t('heroDescription')}
        </p>
      </motion.section>

      {/* Main Speed Test Card */}
      <motion.div
        className="apple-card-solid p-5 sm:p-8 md:p-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        {isDemo && !isRunning && (
          <div className="flex justify-center mb-8">
            <motion.button
              onClick={handleStartTest}
              className="apple-btn-primary-large w-full sm:w-auto"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <Play className="w-6 h-6 mr-2" />
              {t('startRealTest')}
            </motion.button>
          </div>
        )}
        <div className="text-center mb-8">
          <SpeedGauge
            downloadSpeed={displayResults?.downloadSpeed || 0}
            uploadSpeed={displayResults?.uploadSpeed || 0}
            isRunning={isRunning}
            currentTest={currentTest}
            progress={progress}
          />
        </div>

        {/* Error message */}
        <AnimatePresence>
          {error && (
            <motion.div 
              className="mb-6 p-4 bg-apple-red/10 border border-apple-red/20 rounded-apple text-center"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <p className="font-medium text-apple-red">{t('error')}</p>
              <p className="text-sm text-apple-gray-600 mt-1">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Start Test Button */}
        <AnimatePresence mode="wait">
          {!isRunning && !showResults && (
            <motion.div 
              className="flex justify-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <motion.button
                onClick={handleStartTest}
                className="apple-btn-circle text-xl"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="flex flex-col items-center">
                  <Play className="w-10 h-10 mb-2" />
                  <span>{t('startTest')}</span>
                </div>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results Section */}
        <AnimatePresence>
          {(showResults || (isRunning && results)) && displayResults && (
            <motion.div 
              className="space-y-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
            >
              {/* Demo 小提示 */}
              {isDemo && (
                <motion.p 
                  className="text-center text-sm text-apple-gray-500 -mt-4 mb-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {t('demoDataHint')}
                </motion.p>
              )}

              <TestResults results={displayResults} />
              
              {!isRunning && !isDemo && (
                <motion.div 
                  className="flex flex-col sm:flex-row gap-4 justify-center pt-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                    <>
                      <motion.button
                        onClick={handleTestAgain}
                        className="apple-btn-secondary"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <RotateCcw className="w-5 h-5 mr-2" />
                        {t('testAgain')}
                      </motion.button>
                      <ShareResults results={displayResults} />
                    </>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Bottom Section - User Info and Speed Rating */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <UserInfo />
        </motion.div>
        
        <AnimatePresence>
          {showResults && displayResults && (
            <motion.div 
              className="apple-card-solid p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h3 className="text-lg font-semibold text-apple-gray-900 mb-6">
                {t('speedRating')}
                {isDemo && <span className="ml-2 text-xs font-normal text-apple-purple">({t('demoLabel')})</span>}
              </h3>
              <div className="space-y-5">
                {/* Download Rating */}
                <div className="flex justify-between items-center">
                  <span className="text-apple-gray-600">{t('downloadSpeed')}</span>
                  <div className="text-right">
                    <span className="text-apple-gray-900 font-semibold num-display">
                      {displayResults.downloadSpeed.toFixed(1)} {t('mbps')}
                    </span>
                    <div className={`text-sm font-medium ${getSpeedRatingColor(displayResults.downloadSpeed)}`}>
                      {getSpeedRating(displayResults.downloadSpeed)}
                    </div>
                  </div>
                </div>
                
                {/* Progress bar for download */}
                <div className="h-2 bg-apple-gray-100 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-apple-blue rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min((displayResults.downloadSpeed / 200) * 100, 100)}%` }}
                    transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
                  />
                </div>
                
                {/* Upload Rating */}
                <div className="flex justify-between items-center pt-2">
                  <span className="text-apple-gray-600">{t('uploadSpeed')}</span>
                  <div className="text-right">
                    <span className="text-apple-gray-900 font-semibold num-display">
                      {displayResults.uploadSpeed.toFixed(1)} {t('mbps')}
                    </span>
                    <div className={`text-sm font-medium ${getSpeedRatingColor(displayResults.uploadSpeed)}`}>
                      {getSpeedRating(displayResults.uploadSpeed)}
                    </div>
                  </div>
                </div>
                
                {/* Progress bar for upload */}
                <div className="h-2 bg-apple-gray-100 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-apple-green rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min((displayResults.uploadSpeed / 200) * 100, 100)}%` }}
                    transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1], delay: 0.1 }}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Why Choose Us Section */}
      <motion.section
        className="mt-16"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.25 }}
      >
        <h2 className="text-2xl md:text-3xl font-bold text-apple-gray-900 text-center mb-4">
          {t('whyChooseTitle')}
        </h2>
        <p className="text-center text-apple-gray-600 mb-10 max-w-2xl mx-auto">
          {t('whyChooseIntro')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {whyChooseItems.map((item, index) => {
            const Icon = item.icon
            return (
              <motion.div
                key={item.titleKey}
                className="apple-card-solid p-6 hover:shadow-lg transition-shadow duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                whileHover={{ y: -4 }}
              >
                <div className={`w-12 h-12 rounded-2xl bg-apple-gray-100 flex items-center justify-center mb-4 ${item.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-apple-gray-900 mb-2">
                  {t(item.titleKey)}
                </h3>
                <p className="text-apple-gray-600 text-sm leading-relaxed">
                  {t(item.descKey)}
                </p>
              </motion.div>
            )
          })}
        </div>
      </motion.section>

      {/* Core Features Section */}
      <motion.section
        className="mt-16"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <h2 className="text-2xl md:text-3xl font-bold text-apple-gray-900 text-center mb-10">
          {t('featuresTitle')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon
            return (
              <motion.div
                key={feature.titleKey}
                className="apple-card-solid p-6 hover:shadow-lg transition-shadow duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                whileHover={{ y: -4 }}
              >
                <div className={`w-12 h-12 rounded-2xl bg-apple-gray-100 flex items-center justify-center mb-4 ${feature.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-apple-gray-900 mb-2">
                  {t(feature.titleKey)}
                </h3>
                <p className="text-apple-gray-600 text-sm leading-relaxed">
                  {t(feature.descKey)}
                </p>
              </motion.div>
            )
          })}
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section
        className="mt-16"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <h2 className="text-2xl md:text-3xl font-bold text-apple-gray-900 text-center mb-10">
          {t('faqTitle')}
        </h2>
        <div className="apple-card-solid p-6 md:p-8">
          {faqItems.map((item, index) => (
            <FAQItem
              key={item.questionKey}
              questionKey={item.questionKey}
              answerKey={item.answerKey}
              index={index}
            />
          ))}
        </div>
      </motion.section>

      {/* How to Use Section */}
      <motion.section
        className="mt-16"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
      >
        <h2 className="text-2xl md:text-3xl font-bold text-apple-gray-900 text-center mb-4">
          {t('howToUseTitle')}
        </h2>
        <p className="text-center text-apple-gray-600 mb-10 max-w-2xl mx-auto">
          {t('howToUseIntro')}
        </p>
        <div className="apple-card-solid p-6 md:p-8">
          <div className="space-y-4">
            {howToSteps.map((stepKey, index) => (
              <motion.div
                key={stepKey}
                className="flex items-start gap-4"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-apple-blue text-white flex items-center justify-center font-semibold text-sm">
                  {index + 1}
                </div>
                <p className="text-apple-gray-700 pt-1">{t(stepKey)}</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            className="mt-6 p-4 bg-apple-gray-50 rounded-xl border border-apple-gray-200"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.6 }}
          >
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-apple-green flex-shrink-0 mt-0.5" />
              <p className="text-sm text-apple-gray-600">{t('howToTip')}</p>
            </div>
          </motion.div>
        </div>
      </motion.section>
    </div>
  )
}

export default SpeedTest
